import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Trophy, Palette, Globe, UtensilsCrossed, Briefcase, Users,
  CheckCircle2, Clock, Send, X
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const committeeIcons = {
  trophy: Trophy,
  palette: Palette,
  globe: Globe,
  utensils: UtensilsCrossed,
  briefcase: Briefcase,
  users: Users,
};

const committeeColors = [
  { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'bg-blue-500', accent: 'text-blue-600' },
  { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'bg-purple-500', accent: 'text-purple-600' },
  { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'bg-emerald-500', accent: 'text-emerald-600' },
  { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'bg-orange-500', accent: 'text-orange-600' },
  { bg: 'bg-indigo-50', border: 'border-indigo-200', icon: 'bg-indigo-500', accent: 'text-indigo-600' },
  { bg: 'bg-rose-50', border: 'border-rose-200', icon: 'bg-rose-500', accent: 'text-rose-600' },
];

const PortalCommittees = () => {
  const [committees, setCommittees] = useState([]);
  const [myCommittees, setMyCommittees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joinModal, setJoinModal] = useState(null);
  const [joinMessage, setJoinMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [allRes, myRes] = await Promise.all([
        axios.get(`${API}/portal/committees`, { withCredentials: true }),
        axios.get(`${API}/portal/committees/my`, { withCredentials: true }),
      ]);
      setCommittees(allRes.data);
      setMyCommittees(myRes.data);
    } catch (err) {
      // error handled silently
    } finally {
      setLoading(false);
    }
  };

  const getMembershipStatus = (committeeId) => {
    const membership = myCommittees.find(m => m.id === committeeId);
    return membership?.membership_status || null;
  };

  const handleJoin = async () => {
    if (!joinModal) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/portal/committees/join`, {
        committee_id: joinModal.id,
        message: joinMessage,
      }, { withCredentials: true });
      toast.success('Application submitted successfully!');
      setJoinModal(null);
      setJoinMessage('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-4 border-[#002249] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="portal-committees">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-purple-500 rounded-sm flex items-center justify-center">
            <Users className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#002249]">Committees</h1>
        </div>
        <p className="text-slate-600 mt-2">
          Join specialized committees aligned with your interests and contribute to the chamber's mission.
        </p>
      </motion.div>

      {/* My Committees Summary */}
      {myCommittees.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-r from-[#002249] to-[#457B9D] p-5 rounded-sm mb-8 text-white"
          data-testid="my-committees-summary"
        >
          <h2 className="font-bold mb-3">Your Committee Memberships</h2>
          <div className="flex flex-wrap gap-2">
            {myCommittees.map(c => (
              <span
                key={c.id}
                className={`px-3 py-1.5 rounded-sm text-sm font-medium ${
                  c.membership_status === 'approved' ? 'bg-green-500/20 text-green-300' :
                  c.membership_status === 'pending' ? 'bg-yellow-500/20 text-yellow-300' :
                  'bg-red-500/20 text-red-300'
                }`}
              >
                {c.membership_status === 'approved' && <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />}
                {c.membership_status === 'pending' && <Clock className="w-3.5 h-3.5 inline mr-1" />}
                {c.name} — {c.membership_status}
              </span>
            ))}
          </div>
        </motion.div>
      )}

      {/* Committees Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {committees.map((committee, index) => {
          const colors = committeeColors[index % committeeColors.length];
          const Icon = committeeIcons[committee.icon] || Users;
          const status = getMembershipStatus(committee.id);

          return (
            <motion.div
              key={committee.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * index }}
              className={`${colors.bg} border ${colors.border} rounded-sm overflow-hidden hover:shadow-md transition-shadow`}
              data-testid={`committee-card-${index}`}
            >
              <div className="p-5">
                <div className="flex items-center space-x-3 mb-3">
                  <div className={`w-12 h-12 ${colors.icon} rounded-sm flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#002249]">{committee.name}</h3>
                    {committee.leader_name && (
                      <p className="text-xs text-slate-500">Led by {committee.leader_name}</p>
                    )}
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-4">{committee.description}</p>
              </div>
              <div className="px-5 py-3 bg-white/50 border-t border-slate-200/60">
                {status === 'approved' ? (
                  <div className="flex items-center text-sm font-medium text-green-600">
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Active Member
                  </div>
                ) : status === 'pending' ? (
                  <div className="flex items-center text-sm font-medium text-yellow-600">
                    <Clock className="w-4 h-4 mr-2" /> Application Pending
                  </div>
                ) : (
                  <Button
                    onClick={() => setJoinModal(committee)}
                    className="w-full bg-[#002249] hover:bg-[#001630] rounded-sm text-sm"
                    data-testid={`join-committee-${index}`}
                  >
                    <Send className="w-4 h-4 mr-2" /> Request to Join
                  </Button>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {committees.length === 0 && (
        <div className="text-center py-16 text-slate-500">
          <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <p className="text-lg font-medium">No committees available yet</p>
          <p className="text-sm mt-1">Committees are being organized. Check back soon!</p>
        </div>
      )}

      {/* Join Modal */}
      {joinModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setJoinModal(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-sm max-w-md w-full"
            onClick={e => e.stopPropagation()}
            data-testid="join-committee-modal"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-[#002249]">Join {joinModal.name}</h2>
                <button onClick={() => setJoinModal(null)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-slate-600 mb-4">
                Tell us why you'd like to join this committee. Your application will be reviewed by the committee leadership.
              </p>
              <textarea
                value={joinMessage}
                onChange={e => setJoinMessage(e.target.value)}
                placeholder="Why do you want to join this committee? What skills or experience can you contribute?"
                className="w-full border border-slate-200 rounded-sm p-3 text-sm min-h-[120px] focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]"
                data-testid="join-message-input"
              />
              <div className="flex space-x-3 mt-4">
                <Button variant="outline" className="flex-1 rounded-sm" onClick={() => setJoinModal(null)} data-testid="cancel-join-btn">
                  Cancel
                </Button>
                <Button
                  onClick={handleJoin}
                  disabled={submitting}
                  className="flex-1 bg-[#002249] hover:bg-[#001630] rounded-sm"
                  data-testid="submit-join-btn"
                >
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default PortalCommittees;
